import { useState, useRef, useEffect, useCallback } from 'react';
import { fetchRecentChanges } from '../services/generatorBatchApi';

/**
 * Caricamento LAZY del dettaglio modifiche per macrofunzione.
 *
 * Garanzie richieste:
 * - il dettaglio si carica solo all'espansione della scheda (nessun N+1);
 * - stato separato per macroFunctionId (loading/error/data);
 * - cache basata su macroFunctionId + since + until: cambiando intervallo NON si
 *   riusano dati del vecchio periodo;
 * - richieste obsolete annullate (AbortController) e cleanup all'unmount;
 * - "Riprova" dopo errore, senza timer o richieste pendenti residue.
 *
 * @param {Object} range { since, until, limit }
 */
export function useRecentChangeDetails({ since, until, limit = 200 } = {}) {
  const [expanded, setExpanded] = useState(() => new Set());
  // id -> { key, loading, error, data }
  const [entries, setEntries] = useState({});

  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  const abortsRef = useRef(new Map()); // id -> AbortController
  const mountedRef = useRef(true);

  const keyFor = useCallback(
    (id) => `${id}|${since || ''}|${until || ''}`,
    [since, until],
  );

  useEffect(() => {
    mountedRef.current = true;
    const aborts = abortsRef.current;
    return () => {
      mountedRef.current = false;
      aborts.forEach((c) => c.abort());
      aborts.clear();
    };
  }, []);

  const load = useCallback(
    (id) => {
      const key = keyFor(id);
      const prev = abortsRef.current.get(id);
      if (prev) prev.abort();
      const controller = new AbortController();
      abortsRef.current.set(id, controller);

      setEntries((e) => {
        const existing = e[id];
        return {
          ...e,
          [id]: {
            key,
            loading: true,
            error: null,
            // conserva i dati solo se appartengono alla stessa chiave (stesso periodo)
            data: existing && existing.key === key ? existing.data : null,
          },
        };
      });

      fetchRecentChanges(id, { since, until, limit }, controller.signal)
        .then((data) => {
          if (!mountedRef.current) return;
          setEntries((e) => ({ ...e, [id]: { key, loading: false, error: null, data } }));
        })
        .catch((err) => {
          if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
          if (!mountedRef.current) return;
          setEntries((e) => ({
            ...e,
            [id]: { key, loading: false, error: 'Impossibile caricare il dettaglio delle modifiche.', data: null },
          }));
        })
        .finally(() => {
          if (abortsRef.current.get(id) === controller) abortsRef.current.delete(id);
        });
    },
    [keyFor, since, until, limit],
  );

  const toggle = useCallback(
    (id) => {
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
          const c = abortsRef.current.get(id);
          if (c) {
            c.abort();
            abortsRef.current.delete(id);
          }
        } else {
          next.add(id);
          // Carica solo se manca un dato valido per il periodo corrente.
          const entry = entriesRef.current[id];
          if (!entry || entry.key !== keyFor(id)) load(id);
        }
        return next;
      });
    },
    [keyFor, load],
  );

  // Cambio intervallo: invalida i dati delle schede aperte e ricarica.
  useEffect(() => {
    expanded.forEach((id) => {
      const entry = entriesRef.current[id];
      if (!entry || entry.key !== keyFor(id)) load(id);
    });
    // Vogliamo reagire al cambio di intervallo (keyFor), non a ogni load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyFor]);

  const isExpanded = useCallback((id) => expanded.has(id), [expanded]);
  const getDetail = useCallback((id) => entries[id] || null, [entries]);
  const retry = useCallback((id) => load(id), [load]);

  return { isExpanded, toggle, getDetail, retry };
}
